/**
 * Definição de senha nova, a partir do link do e-mail.
 *
 * São dois passos que a pessoa não vê: o `token_hash` da URL vira uma sessão,
 * e só então a senha pode ser trocada. É por isso que a tela verifica o token
 * ao montar, antes de mostrar o formulário — sem sessão válida, o campo de
 * senha seria uma promessa que não se cumpre.
 */

import { type FormEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { Alert } from '@/components/Alert';
import { Brand } from '@/components/Brand';
import { PasswordField } from '@/components/PasswordField';
import { authErrorKey } from '@/features/auth/authErrors';
import { updatePassword, verifyEmailToken } from '@/features/auth/useSession';

const MIN_PASSWORD_LENGTH = 8;

type Stage = 'verifying' | 'ready' | 'invalid';

export default function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'errors', 'common']);
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [stage, setStage] = useState<Stage>('verifying');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const tokenHash = params.get('token_hash');
  const mismatch = confirmation.length > 0 && password !== confirmation;

  useEffect(() => {
    if (!tokenHash) {
      setStage('invalid');
      return;
    }
    let active = true;
    verifyEmailToken(tokenHash, 'recovery')
      .then(() => active && setStage('ready'))
      .catch(() => active && setStage('invalid'));
    return () => {
      active = false;
    };
  }, [tokenHash]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      setErrorKey('auth:field.passwordTooShort');
      return;
    }
    if (password !== confirmation) {
      setErrorKey('auth:field.passwordMismatch');
      return;
    }

    setErrorKey(null);
    setBusy(true);
    try {
      await updatePassword(password);
      // A verificação do token já deixou a sessão ativa: com a senha trocada,
      // a pessoa entra direto. Mandá-la ao login seria pedir que digitasse
      // agora a senha que acabou de escolher.
      navigate('/today', { replace: true });
    } catch (error) {
      setErrorKey(authErrorKey(error));
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'verifying') {
    return (
      <main className="mh-shell items-center justify-center px-5" aria-busy="true">
        <p className="text-(--color-sand-700)">{t('common:state.loading')}</p>
      </main>
    );
  }

  if (stage === 'invalid') {
    return (
      <main className="mh-shell items-center justify-center px-5 py-10 text-center">
        <h1 className="text-2xl">{t('auth:reset.invalidTitle')}</h1>
        <p className="mt-2 mb-8 text-(--color-sand-700)">{t('auth:reset.invalidBody')}</p>
        <Link to="/forgot-password" className="mh-button mh-button--primary">
          {t('auth:reset.invalidCta')}
        </Link>
      </main>
    );
  }

  return (
    <main className="mh-shell justify-center px-5 py-10">
      <Brand />
      <h1 className="text-3xl">{t('auth:reset.title')}</h1>
      <p className="mt-1 mb-8 text-(--color-sand-700)">{t('auth:reset.subtitle')}</p>

      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4">
        <PasswordField
          id="new-password"
          label={t('auth:reset.newPassword')}
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          minLength={MIN_PASSWORD_LENGTH}
        />

        <div>
          <PasswordField
            id="new-confirmation"
            label={t('auth:signUp.confirmPassword')}
            value={confirmation}
            onChange={setConfirmation}
            autoComplete="new-password"
            invalid={mismatch}
            describedBy={mismatch ? 'reset-mismatch' : undefined}
          />
          {mismatch ? (
            <p
              id="reset-mismatch"
              role="alert"
              className="mt-1.5 text-sm font-medium text-(--color-danger)"
            >
              {t('auth:field.passwordMismatch')}
            </p>
          ) : null}
        </div>

        {errorKey ? (
          <Alert tone="error" live="assertive">
            {t(errorKey)}
          </Alert>
        ) : null}

        <button
          type="submit"
          className="mh-button mh-button--primary mt-2"
          disabled={busy || mismatch}
        >
          {busy ? t('common:state.loading') : t('auth:reset.submit')}
        </button>
      </form>
    </main>
  );
}
